// TraitSelector, visual trait picker for AstroMatch
// Replaces photo upload entirely. All fields are optional.
// Game-like pill selectors with gold border + checkmark on selection.

import type { FC } from 'react';
import { Check } from 'lucide-react';

// ─────────────────────────────────────────
// Inline SVG face shape illustrations
// ─────────────────────────────────────────

function FaceOval() {
  return (
    <svg width="36" height="44" viewBox="0 0 36 44" fill="none" xmlns="http://www.w3.org/2000/svg">
      <ellipse cx="18" cy="22" rx="14" ry="19" stroke="currentColor" strokeWidth="2" fill="none"/>
    </svg>
  );
}
function FaceRedondo() {
  return (
    <svg width="40" height="44" viewBox="0 0 40 44" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="20" cy="22" r="17" stroke="currentColor" strokeWidth="2" fill="none"/>
    </svg>
  );
}
function FaceQuadrado() {
  return (
    <svg width="40" height="44" viewBox="0 0 40 44" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="4" y="4" width="32" height="36" rx="4" stroke="currentColor" strokeWidth="2" fill="none"/>
    </svg>
  );
}
function FaceCoracao() {
  return (
    <svg width="40" height="44" viewBox="0 0 40 44" fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* Wide forehead, narrow pointed chin */}
      <path d="M4 10 Q4 4 20 4 Q36 4 36 10 L36 24 Q36 36 20 42 Q4 36 4 24 Z"
        stroke="currentColor" strokeWidth="2" fill="none" strokeLinejoin="round"/>
    </svg>
  );
}
function FaceDiamante() {
  return (
    <svg width="40" height="44" viewBox="0 0 40 44" fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* Narrow top & bottom, widest in middle */}
      <path d="M20 2 L36 22 L20 42 L4 22 Z"
        stroke="currentColor" strokeWidth="2" fill="none" strokeLinejoin="round"/>
    </svg>
  );
}

const FACE_SHAPE_SVGS: Partial<Record<string, FC>> = {
  'Oval':     FaceOval,
  'Redondo':  FaceRedondo,
  'Quadrado': FaceQuadrado,
  'Coração':  FaceCoracao,
  'Diamante': FaceDiamante,
};

// ─────────────────────────────────────────
// Types
// ─────────────────────────────────────────

export interface TraitSelections {
  eye_color?:       string;
  face_shape?:      string;
  nose_type?:       string;
  lips_type?:       string;
  hair_type?:       string;
  hair_color?:      string;
  height_category?: string;
  energy_type?:     string;
}

interface TraitSelectorProps {
  value:    TraitSelections;
  onChange: (traits: TraitSelections) => void;
}

// ─────────────────────────────────────────
// Section data
// ─────────────────────────────────────────

const SECTIONS = [
  {
    key:   'eye_color' as const,
    title: '👁️ Olhos',
    options: [
      { label: '🟤 Castanhos', value: 'Castanhos' },
      { label: '🔵 Azuis',     value: 'Azuis'     },
      { label: '🟢 Verdes',    value: 'Verdes'    },
      { label: '⚫ Pretos',    value: 'Pretos'    },
      { label: '🩶 Cinzas',    value: 'Cinzas'    },
      { label: 'Não sei',      value: ''          },
    ],
  },
  {
    key:   'face_shape' as const,
    title: '🔷 Formato do rosto',
    options: [
      { label: 'Oval',      value: 'Oval'      },
      { label: 'Redondo',   value: 'Redondo'   },
      { label: 'Quadrado',  value: 'Quadrado'  },
      { label: 'Coração',   value: 'Coração'   },
      { label: 'Diamante',  value: 'Diamante'  },
      { label: 'Não sei',   value: ''          },
    ],
  },
  {
    key:   'nose_type' as const,
    title: '👃 Nariz',
    options: [
      { label: 'Pequeno e fino',    value: 'Pequeno e fino'    },
      { label: 'Grande e marcante', value: 'Grande e marcante' },
      { label: 'Arredondado',       value: 'Arredondado'       },
      { label: 'Afilado',           value: 'Afilado'           },
      { label: 'Não sei',           value: ''                  },
    ],
  },
  {
    key:   'lips_type' as const,
    title: '💋 Boca / Lábios',
    options: [
      { label: 'Lábios finos',   value: 'Lábios finos'   },
      { label: 'Lábios carnudos', value: 'Lábios carnudos' },
      { label: 'Boca larga',     value: 'Boca larga'     },
      { label: 'Boca pequena',   value: 'Boca pequena'   },
      { label: 'Não sei',        value: ''               },
    ],
  },
  {
    key:   'hair_type' as const,
    title: '💇 Cabelo, tipo',
    options: [
      { label: 'Liso',     value: 'Liso'     },
      { label: 'Ondulado', value: 'Ondulado' },
      { label: 'Cacheado', value: 'Cacheado' },
      { label: 'Crespo',   value: 'Crespo'   },
      { label: 'Careca',   value: 'Careca'   },
      { label: 'Não sei',  value: ''         },
    ],
  },
  {
    key:   'hair_color' as const,
    title: '🎨 Cabelo, cor',
    options: [
      { label: 'Loiro',     value: 'Loiro'     },
      { label: 'Castanho',  value: 'Castanho'  },
      { label: 'Preto',     value: 'Preto'     },
      { label: 'Ruivo',     value: 'Ruivo'     },
      { label: 'Grisalho',  value: 'Grisalho'  },
      { label: 'Não sei',   value: ''          },
    ],
  },
  {
    key:   'height_category' as const,
    title: '📏 Estatura',
    options: [
      { label: '🔽 Baixo',      value: 'Baixo'      },
      { label: '▪️ Médio',      value: 'Médio'      },
      { label: '🔼 Alto',       value: 'Alto'       },
      { label: 'Muito alto',   value: 'Muito alto' },
      { label: 'Não sei',      value: ''           },
    ],
  },
  {
    key:   'energy_type' as const,
    title: '✨ Energia / Presença',
    options: [
      { label: '☀️ Muito extrovertido',  value: 'Muito extrovertido'  },
      { label: '🌤 Extrovertido',         value: 'Extrovertido'         },
      { label: '⛅ Equilibrado',          value: 'Equilibrado'          },
      { label: '🌥 Introvertido',         value: 'Introvertido'         },
      { label: '🌙 Muito introvertido',   value: 'Muito introvertido'   },
      { label: 'Não sei',                value: ''                     },
    ],
  },
] as const;

// ─────────────────────────────────────────
// Pill button
// ─────────────────────────────────────────

function Pill({
  label,
  selected,
  onClick,
}: {
  label:    string;
  selected: boolean;
  onClick:  () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display:         'inline-flex',
        alignItems:      'center',
        gap:             '0.3rem',
        padding:         '0.45rem 0.85rem',
        borderRadius:    '999px',
        border:          selected ? '2px solid var(--color-gold)' : '1px solid var(--border-subtle)',
        background:      selected ? 'rgba(245,200,66,0.12)' : 'var(--surface-card)',
        color:           selected ? 'var(--color-gold)' : 'var(--text-secondary)',
        fontSize:        'var(--text-caption)',
        fontWeight:      selected ? 600 : 400,
        cursor:          'pointer',
        transition:      'all 0.15s ease',
        flexShrink:      0,
      }}
    >
      {selected && <Check size={12} />}
      {label}
    </button>
  );
}

// ─────────────────────────────────────────
// Section
// ─────────────────────────────────────────

function Section({
  title,
  sectionKey,
  options,
  selected,
  onSelect,
}: {
  title:      string;
  sectionKey: keyof TraitSelections;
  options:    readonly { label: string; value: string }[];
  selected:   string | undefined;
  onSelect:   (key: keyof TraitSelections, value: string) => void;
}) {
  return (
    <div style={{ marginBottom: '1.25rem' }}>
      <p style={{ fontSize: 'var(--text-label)', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '0.5rem' }}>
        {title}
      </p>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
        {options.map(opt => (
          <Pill
            key={opt.label}
            label={opt.label}
            selected={selected === opt.value && (opt.value !== '' || selected === '')}
            onClick={() => onSelect(sectionKey, opt.value === selected ? '__clear__' : opt.value)}
          />
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────
// Main component
// ─────────────────────────────────────────

export function TraitSelector({ value, onChange }: TraitSelectorProps) {
  // Count how many sections have a selection (excluding empty "Não sei")
  const selectedCount = SECTIONS.filter(s => value[s.key] !== undefined && value[s.key] !== '').length;
  const totalSections = SECTIONS.length;

  function handleSelect(key: keyof TraitSelections, val: string) {
    if (val === '__clear__') {
      const next = { ...value };
      delete next[key];
      onChange(next);
    } else {
      onChange({ ...value, [key]: val });
    }
  }

  return (
    <div>
      {/* Progress indicator */}
      <div style={{
        display:         'flex',
        alignItems:      'center',
        justifyContent:  'space-between',
        marginBottom:    '1.25rem',
        padding:         '0.6rem 0.9rem',
        background:      'var(--surface-card)',
        borderRadius:    'var(--radius-md)',
        border:          '1px solid var(--border-subtle)',
      }}>
        <span style={{ fontSize: 'var(--text-caption)', color: 'var(--text-secondary)' }}>
          {selectedCount > 0
            ? `${selectedCount} de ${totalSections} características selecionadas`
            : 'Selecione as características (tudo é opcional ✦)'}
        </span>
        <div style={{
          width: '80px', height: '4px',
          background: 'var(--border-subtle)', borderRadius: '999px', overflow: 'hidden',
        }}>
          <div style={{
            height: '100%', width: `${(selectedCount / totalSections) * 100}%`,
            background: 'var(--color-gold)', borderRadius: '999px', transition: 'width 0.3s ease',
          }} />
        </div>
      </div>

      {/* Sections */}
      {SECTIONS.map(section => {
        if (section.key === 'face_shape') {
          // Face shape: render as visual cards with SVG illustrations
          const selected = value[section.key];
          return (
            <div key={section.key} style={{ marginBottom: '1.25rem' }}>
              <p style={{ fontSize: 'var(--text-label)', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '0.75rem' }}>
                {section.title}
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.6rem' }}>
                {section.options.map(opt => {
                  const isSelected = selected === opt.value && (opt.value !== '' || selected === '');
                  const SvgIcon = FACE_SHAPE_SVGS[opt.value];
                  return (
                    <button
                      key={opt.label}
                      type="button"
                      onClick={() => handleSelect(section.key, opt.value === selected ? '__clear__' : opt.value)}
                      style={{
                        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.35rem',
                        padding: SvgIcon ? '0.6rem 0.75rem' : '0.45rem 0.85rem',
                        borderRadius: SvgIcon ? 'var(--radius-md)' : '999px',
                        border: isSelected ? '2px solid var(--color-gold)' : '1px solid var(--border-subtle)',
                        background: isSelected ? 'rgba(245,200,66,0.12)' : 'var(--surface-card)',
                        color: isSelected ? 'var(--color-gold)' : 'var(--text-secondary)',
                        fontSize: 'var(--text-caption)',
                        fontWeight: isSelected ? 600 : 400,
                        cursor: 'pointer',
                        transition: 'all 0.15s ease',
                        minWidth: SvgIcon ? '70px' : undefined,
                        position: 'relative',
                      }}
                    >
                      {isSelected && !SvgIcon && (
                        <Check size={12} style={{ position: 'absolute', top: '0.3rem', right: '0.3rem' }} />
                      )}
                      {SvgIcon && (
                        <span style={{ color: isSelected ? 'var(--color-gold)' : 'var(--text-muted)', lineHeight: 0 }}>
                          <SvgIcon />
                        </span>
                      )}
                      <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                        {isSelected && SvgIcon && <Check size={10} />}
                        {opt.label}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        }
        return (
          <Section
            key={section.key}
            title={section.title}
            sectionKey={section.key}
            options={section.options}
            selected={value[section.key]}
            onSelect={handleSelect}
          />
        );
      })}

      {selectedCount === 0 && (
        <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', fontStyle: 'italic', marginTop: '0.25rem' }}>
          Não se preocupe, a análise funciona mesmo sem selecionar nada.
          Quanto mais detalhes, mais rica a leitura fisionômica.
        </p>
      )}
    </div>
  );
}
